function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useCallback, useState } from 'react';
import { useOverlayContext } from 'seagull';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { getTestIds } from 'seagull/utils/TestIds';
import { useIoContext, useStoreActions } from "../../contexts";
export const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'];
export const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes
export const DEFAULT_MAX_TOTAL_SIZE = 24 * 1024 * 1024; // 24MB in bytes

export const AttachFilesTestIds = getTestIds('AttachFilesTestIds');
export function useAttachFiles(params) {
  const {
    maxTotalSize = DEFAULT_MAX_TOTAL_SIZE,
    maxFileSize = MAX_FILE_SIZE,
    initialAttachments = [],
    onFilesUploaded,
    onRemove
  } = params;
  const {
    api
  } = useStoreActions();
  const {
    showErrorToast
  } = useOverlayContext();
  const {
    uploadFileToCloudStorage
  } = useIoContext();
  const [isUploading, setIsUploading] = useState(false);
  const [attachments, setAttachments] = useState(initialAttachments);
  const uploadFilesToGCS = async files => {
    const filesMap = {};
    const filesData = [];
    files.forEach(file => {
      const id = generateUniqueId('attachment');
      filesData.push({
        id,
        displayName: file.name,
        size: file.size
      });
      filesMap[id] = file;
    });
    const {
      urls
    } = await api.business.getUploadUrls(filesData);
    await Promise.all(urls.map(_ref => {
      let {
        id,
        url,
        headers
      } = _ref;
      const file = filesMap[id];
      if (!file) throw new Error("File not found for id ".concat(id));
      return uploadFileToCloudStorage(url, file, headers);
    }));
    return filesData;
  };
  const handleUpload = async files => {
    const loadingAttachments = files.map(file => ({
      id: generateUniqueId('temp'),
      name: file.name,
      size: file.size,
      loading: true
    }));
    setAttachments(prev => [...prev, ...loadingAttachments]);
    setIsUploading(true);
    try {
      const uploadedData = await uploadFilesToGCS(files);
      const completedAttachments = await onFilesUploaded(uploadedData);
      setAttachments(prev => {
        const nonLoading = prev.filter(att => !loadingAttachments.some(la => la.id === att.id));
        return [...nonLoading, ...completedAttachments.map(att => _objectSpread(_objectSpread({}, att), {}, {
          loading: false
        }))];
      });
    } catch (error) {
      setAttachments(prev => prev.filter(att => !loadingAttachments.some(la => la.id === att.id)));
      showErrorToast('Failed to upload files', AttachFilesTestIds.UploadFailedErrorToast());
    } finally {
      setIsUploading(false);
    }
  };
  const processFiles = files => {
    if (!files || files.length === 0) return;
    const allFiles = Array.from(files);
    const validFiles = allFiles.filter(file => {
      var _file$name$split$pop;
      const extension = (_file$name$split$pop = file.name.split('.').pop()) === null || _file$name$split$pop === void 0 ? void 0 : _file$name$split$pop.toLowerCase();
      return extension && ALLOWED_EXTENSIONS.includes(extension);
    });
    if (validFiles.length !== allFiles.length) {
      showErrorToast('Unsupported file type', AttachFilesTestIds.UnsupportedFileTypeErrorToast());
      return;
    }
    const totalSize = [...attachments, ...validFiles].reduce((sum, file) => sum + file.size, 0);
    if (totalSize > maxTotalSize) {
      showErrorToast("Attachments exceeded the ".concat(maxTotalSize / (1024 * 1024), "MB upload limit"), AttachFilesTestIds.TotalSizeExceededErrorToast());
      return;
    }
    const filesWithinSizeLimit = validFiles.filter(file => file.size <= maxFileSize);
    if (filesWithinSizeLimit.length !== validFiles.length) {
      showErrorToast("File exceeds the ".concat(maxFileSize / (1024 * 1024), "MB size limit"), AttachFilesTestIds.FileSizeExceededErrorToast());
      return;
    }
    handleUpload(filesWithinSizeLimit);
  };
  const openFilePicker = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = ALLOWED_EXTENSIONS.map(ext => ".".concat(ext)).join(',');
    input.onchange = () => {
      processFiles(input.files);
      input.remove();
    };
    input.click();
  };
  const handleRemove = useCallback(async index => {
    const attachment = attachments[index];
    if (!attachment || attachment.loading) return;
    if (!onRemove) return;
    const previousAttachments = attachments;
    setAttachments(prev => prev.filter((_, i) => i !== index));
    try {
      await onRemove(attachment);
    } catch (_unused) {
      setAttachments(previousAttachments);
      showErrorToast('Failed to remove attachment', AttachFilesTestIds.RemoveFailedErrorToast());
    }
  }, [attachments, onRemove, showErrorToast]);
  return {
    attachments,
    openFilePicker,
    processFiles,
    handleRemove,
    allowedExtensions: ALLOWED_EXTENSIONS,
    isUploading
  };
}